/**
 * ChequeoForm - Formulario para responder lista de chequeo de 17 ítems
 * Features:
 * - 17 ítems organizados en 4 secciones
 * - Respuestas Sí/No por ítem
 * - Comentarios opcionales
 * - Validación de completitud
 * - Guardar borrador
 */
import { useState, useEffect } from 'react';
import { CheckCircle, XCircle, MessageSquare, AlertCircle, Save } from 'lucide-react';
import { chequeoAPI } from '../../api';
import { Button, Loading, Alert } from '../common';
import useAuthStore from '../../store/authStore';

const SECCIONES = {
  I: {
    nombre: 'Privacidad, Datos Personales y Transparencia',
    color: 'blue',
  },
  II: {
    nombre: 'Algoritmos, Sesgo y Equidad',
    color: 'purple',
  },
  III: {
    nombre: 'Impacto Social y Comunitario',
    color: 'green',
  },
  IV: {
    nombre: 'Gobernanza y Cultura de Compliance',
    color: 'orange',
  },
};

const ChequeoForm = ({ evaluacionId = null, onSubmit, onCancel }) => {
  const { user } = useAuthStore();
  
  // Estado
  const [plantilla, setPlantilla] = useState([]);
  const [respuestas, setRespuestas] = useState({});
  const [comentarios, setComentarios] = useState({});
  const [notasGenerales, setNotasGenerales] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  // Cargar plantilla
  useEffect(() => {
    loadPlantilla();
  }, []);

  const loadPlantilla = async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await chequeoAPI.getPlantilla();
      const items = response.data;
      
      setPlantilla(items);

      // Si hay una evaluación existente, cargar respuestas
      if (evaluacionId) {
        const evalResponse = await chequeoAPI.getEvaluacion(evaluacionId);
        const evaluacionData = evalResponse.data;
        
        // Convertir respuestas a formato del formulario
        if (evaluacionData.respuestas && evaluacionData.respuestas.length > 0) {
          const respuestasMap = {};
          const comentariosMap = {};
          
          evaluacionData.respuestas.forEach((r) => {
            respuestasMap[r.id_item] = r.respuesta;
            if (r.comentario) {
              comentariosMap[r.id_item] = r.comentario;
            }
          });
          
          setRespuestas(respuestasMap);
          setComentarios(comentariosMap);
        }
        
        if (eval.notas_generales) {
          setNotasGenerales(evaluacionData.notas_generales);
        }
      }
    } catch (err) {
      console.error('Error al cargar plantilla:', err);
      setError(err.message || 'Error al cargar plantilla');
    } finally {
      setLoading(false);
    }
  };

  // Agrupar ítems por sección
  const itemsPorSeccion = plantilla.reduce((acc, item) => {
    const seccion = item.seccion;
    if (!acc[seccion]) {
      acc[seccion] = [];
    }
    acc[seccion].push(item);
    return acc;
  }, {});

  // Handlers
  const handleRespuestaChange = (itemId, valor) => {
    setRespuestas({ ...respuestas, [itemId]: valor });
  };

  const handleComentarioChange = (itemId, valor) => {
    setComentarios({ ...comentarios, [itemId]: valor });
  };

  const validateForm = () => {
    // Validar que todos los ítems tengan respuesta
    const itemsRespondidos = Object.keys(respuestas).length;
    const totalItems = plantilla.length;
    
    if (itemsRespondidos < totalItems) {
      setError(`Por favor responde todos los ítems (${itemsRespondidos}/${totalItems})`);
      return false;
    }
    
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    try {
      setSaving(true);
      setError(null);

      // Crear evaluación si no existe
      let evalId = evaluacionId;
      
      if (!evalId) {
        const createResponse = await chequeoAPI.createEvaluacion({
          notas_generales: notasGenerales,
        });
        evalId = createResponse.data.id_evaluacion;
      }

      // Guardar respuestas en batch
      const respuestasArray = Object.keys(respuestas).map((itemId) => ({
        id_item: parseInt(itemId),
        respuesta: respuestas[itemId],
        comentario: comentarios[itemId] || null,
      }));

      await chequeoAPI.guardarRespuestasBatch(evalId, respuestasArray);

      // Marcar como completada
      await chequeoAPI.updateEvaluacion(evalId, {
        completada: true,
        notas_generales: notasGenerales,
      });

      setSuccess('Evaluación guardada correctamente');
      
      if (onSubmit) {
        setTimeout(() => onSubmit({ id_evaluacion: evalId }), 1000);
      }
    } catch (err) {
      console.error('Error al guardar evaluación:', err);
      setError(err.message || 'Error al guardar evaluación');
    } finally {
      setSaving(false);
    }
  };

  const handleGuardarBorrador = async () => {
    try {
      setSaving(true);
      setError(null);

      let evalId = evaluacionId;
      
      // Crear evaluación si no existe
      if (!evalId) {
        const createResponse = await chequeoAPI.createEvaluacion({
          notas_generales: notasGenerales,
        });
        evalId = createResponse.data.id_evaluacion;
      }

      // Guardar solo las respuestas que existen
      if (Object.keys(respuestas).length > 0) {
        const respuestasArray = Object.keys(respuestas).map((itemId) => ({
          id_item: parseInt(itemId),
          respuesta: respuestas[itemId],
          comentario: comentarios[itemId] || null,
        }));

        await chequeoAPI.guardarRespuestasBatch(evalId, respuestasArray);
      }

      // Actualizar notas generales
      await chequeoAPI.updateEvaluacion(evalId, {
        completada: false,
        notas_generales: notasGenerales,
      });

      setSuccess('Borrador guardado correctamente');
      
      if (onSubmit) {
        setTimeout(() => onSubmit({ id_evaluacion: evalId }), 1000);
      }
    } catch (err) {
      console.error('Error al guardar borrador:', err);
      setError(err.message || 'Error al guardar borrador');
    } finally {
      setSaving(false);
    }
  };

  const getProgress = () => {
    const respondidos = Object.keys(respuestas).length;
    const total = plantilla.length;
    return total > 0 ? Math.round((respondidos / total) * 100) : 0;
  };

  if (loading) {
    return (
      <div className="p-8">
        <Loading size="lg" text="Cargando plantilla..." />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Alertas */}
      {error && (
        <Alert
          type="error"
          title="Error"
          message={error}
          onClose={() => setError(null)}
        />
      )}

      {success && (
        <Alert
          type="success"
          title="Éxito"
          message={success}
          onClose={() => setSuccess(null)}
        />
      )}

      {/* Barra de progreso */}
      <div className="bg-gray-50 p-4 rounded-lg">
        <div className="flex items-center justify-between mb-2">
          <span className="text-sm font-medium text-gray-700">
            Progreso de Evaluación
          </span>
          <span className="text-sm font-medium text-primary-600">
            {getProgress()}% ({Object.keys(respuestas).length}/{plantilla.length})
          </span>
        </div>
        <div className="w-full bg-gray-200 rounded-full h-2">
          <div
            className="bg-primary-600 h-2 rounded-full transition-all duration-300"
            style={{ width: `${getProgress()}%` }}
          />
        </div>
      </div>

      {/* Formulario */}
      <form onSubmit={handleSubmit} className="space-y-8">
        {/* Ítems por sección */}
        {Object.keys(SECCIONES).map((seccionKey) => {
          const seccion = SECCIONES[seccionKey];
          const items = itemsPorSeccion[seccionKey] || [];

          if (items.length === 0) return null;

          return (
            <div key={seccionKey} className="space-y-4">
              {/* Header de sección */}
              <div
                className={`bg-${seccion.color}-50 border-l-4 border-${seccion.color}-500 p-4 rounded-r-lg`}
              >
                <h3 className={`text-lg font-semibold text-${seccion.color}-900`}>
                  Sección {seccionKey}: {seccion.nombre}
                </h3>
              </div>

              {/* Ítems */}
              <div className="space-y-6">
                {items.map((item, index) => {
                  const itemId = item.id_item;
                  const respuesta = respuestas[itemId];

                  return (
                    <div
                      key={itemId}
                      className="bg-white border border-gray-200 rounded-lg p-4 hover:border-gray-300 transition-colors"
                    >
                      {/* Pregunta */}
                      <div className="mb-4">
                        <label className="block font-medium text-gray-900 mb-1">
                          {index + 1}. {item.descripcion}
                        </label>
                        {item.riesgo_dolor && (
                          <p className="text-sm text-gray-500 mt-1">
                            <AlertCircle className="inline h-4 w-4 mr-1" />
                            {item.riesgo_dolor}
                          </p>
                        )}
                      </div>

                      {/* Opciones de respuesta */}
                      <div className="flex gap-4 mb-4">
                        <button
                          type="button"
                          onClick={() => handleRespuestaChange(itemId, true)}
                          className={`flex-1 flex items-center justify-center gap-2 px-4 py-3 rounded-lg border-2 transition-all ${
                            respuesta === true
                              ? 'border-green-500 bg-green-50 text-green-700'
                              : 'border-gray-300 hover:border-green-300 text-gray-700'
                          }`}
                        >
                          <CheckCircle
                            className={`h-5 w-5 ${
                              respuesta === true ? 'text-green-600' : 'text-gray-400'
                            }`}
                          />
                          <span className="font-medium">Sí</span>
                        </button>

                        <button
                          type="button"
                          onClick={() => handleRespuestaChange(itemId, false)}
                          className={`flex-1 flex items-center justify-center gap-2 px-4 py-3 rounded-lg border-2 transition-all ${
                            respuesta === false
                              ? 'border-red-500 bg-red-50 text-red-700'
                              : 'border-gray-300 hover:border-red-300 text-gray-700'
                          }`}
                        >
                          <XCircle
                            className={`h-5 w-5 ${
                              respuesta === false ? 'text-red-600' : 'text-gray-400'
                            }`}
                          />
                          <span className="font-medium">No</span>
                        </button>
                      </div>

                      {/* Comentario opcional */}
                      <div>
                        <label className="flex items-center text-sm text-gray-700 mb-2">
                          <MessageSquare className="h-4 w-4 mr-1" />
                          Comentario adicional (opcional)
                        </label>
                        <textarea
                          value={comentarios[itemId] || ''}
                          onChange={(e) =>
                            handleComentarioChange(itemId, e.target.value)
                          }
                          placeholder="Agrega observaciones o contexto adicional..."
                          rows={2}
                          className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                        />
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })}

        {/* Notas generales */}
        <div className="bg-gray-50 p-4 rounded-lg">
          <label className="block font-medium text-gray-900 mb-2">
            Notas Generales de la Evaluación (opcional)
          </label>
          <textarea
            value={notasGenerales}
            onChange={(e) => setNotasGenerales(e.target.value)}
            placeholder="Agrega observaciones generales sobre la evaluación..."
            rows={4}
            className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
          />
        </div>

        {/* Botones */}
        <div className="flex gap-3 justify-end pt-4 border-t">
          {onCancel && (
            <Button
              type="button"
              variant="outline"
              onClick={onCancel}
              disabled={saving}
            >
              Cancelar
            </Button>
          )}

          <Button
            type="button"
            variant="secondary"
            onClick={handleGuardarBorrador}
            disabled={saving || Object.keys(respuestas).length === 0}
            icon={Save}
          >
            Guardar Borrador
          </Button>

          <Button
            type="submit"
            variant="primary"
            disabled={saving || getProgress() !== 100}
            loading={saving}
          >
            Completar Evaluación
          </Button>
        </div>

        {getProgress() !== 100 && (
          <p className="text-sm text-gray-500 text-center">
            Responde todos los ítems para completar la evaluación
          </p>
        )}
      </form>
    </div>
  );
};

export default ChequeoForm;
